import React, { useState } from 'react';
import {
  Button,
  ButtonColor,
  ButtonSize,
  usePopupManager,
} from 'ui-builder';
import {
  PROJECT_SCRATCHPAD_UPDATED_EVENT,
  ProjectPermission,
  ProjectScratchpad as ProjectScratchpadType,
  useProjectScratchpadService,
} from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';
import { useAuthorizationChecker } from 'authorization-scope';
import { IconButton, IconType } from 'features/nekst-widgets';
import BorderedBox from '../../../shared/web/layout/BorderedBox';
import { EditScratchpadForm } from './EditScratchpadForm';
import { ScratchpadContent } from './ScratchpadContent';
import { ScratchpadPopup } from './ScratchpadPopup';

import styles from './ProjectScratchpad.module.scss';

interface Props {
  projectId: number;
}

export default function ProjectScratchpad(props: Props) {
  const [isEditing, setIsEditing] = useState(false);

  const scratchpadService = useProjectScratchpadService();
  const { isGranted } = useAuthorizationChecker();
  const popupManager = usePopupManager();

  const { data, refreshData } = useDataLoaderNew<ProjectScratchpadType | null>(
    () => scratchpadService.get(props.projectId),
    {
      dependencies: [props.projectId],
      events: [PROJECT_SCRATCHPAD_UPDATED_EVENT],
    },
  );

  const canEdit = isGranted(ProjectPermission.UPDATE);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSaved = async () => {
    setIsEditing(false);
    refreshData();
  };

  const handleExpandClick = () => {
    popupManager.openPopup(
      <ScratchpadPopup
        projectId={props.projectId}
        text={data?.text}
        canEdit={canEdit}
        onSaved={async () => { refreshData(); }}
      />
    );
  };

  const headerButtons = !isEditing ? (
    <div className={styles.headerButtons}>
      {canEdit && (
        <Button
          text="Edit"
          size={ButtonSize.SMALL}
          color={ButtonColor.BLUE}
          isFilled
          onClick={handleEditClick}
        />
      )}
      <IconButton
        type={IconType.EXPAND}
        onClick={handleExpandClick}
        title="Expand"
      />
    </div>
  ) : undefined;

  return (
    <div className={styles.wrapper}>
      <BorderedBox
        title="Scratchpad"
        headerRight={headerButtons}
        testId="project-scratchpad"
      >
        {isEditing ? (
          <EditScratchpadForm
            projectId={props.projectId}
            initialText={data?.text}
            onCancel={handleCancel}
            onSaved={handleSaved}
          />
        ) : (
          <ScratchpadContent text={data?.text} />
        )}
      </BorderedBox>
    </div>
  );
}
